import { useRef } from 'react';

/**
 * Generates a deterministic string representation of an object's selected fields.
 * Useful for React's useMemo dependency arrays when memoizing based on specific object properties.
 *
 * @template T - Type of the object, must be a record with string keys and unknown values
 * @param obj - The object to generate a token from. Can be null or undefined.
 * @param fields - Array of field names from the object to include in the token
 * @returns A deterministic string representation of the selected fields
 *
 * @example
 * ```ts
 * // Use as dependency in useMemo
 * const memoizedValue = useMemo(() => {
 *   // expensive computation using user.id and user.name
 * }, [generateMemoToken(user, ['id', 'name'])]);
 * ```
 */
export const generateMemoToken = <T extends Record<string, unknown>>(
  obj: T | null | undefined,
  fields: (keyof T)[] | keyof T
): string => {
  const fieldArray = Array.isArray(fields) ? fields : [fields];

  const selectedFields = fieldArray.reduce(
    (acc, field) => ({
      ...acc,
      [field]: obj?.[field],
    }),
    {}
  );

  const sortedKeys = Object.keys(selectedFields).sort();

  return JSON.stringify(selectedFields, sortedKeys);
};

/**
 * Generates a deterministic token for an array of objects based on selected fields.
 * This is useful to stabilize dependency arrays for useMemo/useEffect when the array
 * identity changes frequently while its relevant contents remain the same.
 *
 * The order of elements is preserved; if elements are re-ordered the token will change.
 *
 * @template T - Type of the array elements, must be a record with string keys
 * @param arr - The array to generate a token from
 * @param fields - Field or fields from each element to include in the token
 * @returns A deterministic string token representing the array contents
 *
 * @example
 * ```ts
 * const itemsToken = generateArrayMemoToken(items, ['id', 'createdAt']);
 * const filtered = useMemo(() => filter(items), [itemsToken]);
 * ```
 */
export const generateArrayMemoToken = <T extends Record<string, unknown>>(
  arr: T[] | null | undefined,
  fields: (keyof T)[] | keyof T
): string => {
  if (!arr || arr.length === 0) return '[]';

  const fieldArray = Array.isArray(fields) ? fields : [fields];

  // Map each element to a compact, deterministic representation using selected fields
  const parts = arr.map((el) =>
    fieldArray.map((f) => `${String(f)}:${String(el?.[f])}`).join('|')
  );

  // Include length to avoid collisions on prefix-equal sequences
  return `${arr.length};${parts.join(',')}`;
};

/**
 * Returns a stable reference for an array when its selected content (fields) hasn't changed.
 * Useful to avoid unnecessary re-computations without suppressing eslint exhaustive-deps.
 *
 * @template T - Type of array elements
 * @param arr - Source array
 * @param fields - Fields that determine content equality for stability
 * @returns The same array reference if content token didn't change; otherwise the new array
 */
export const useStableArrayByToken = <T extends Record<string, unknown>>(
  arr: T[] | null | undefined,
  fields: (keyof T)[] | keyof T
): T[] => {
  const ref = useRef<{ token: string; value: T[] }>({
    token: '[]',
    value: [] as T[],
  });

  const token = generateArrayMemoToken(arr, fields);
  const value = (arr || []) as T[];

  if (ref.current.token === token) {
    return ref.current.value;
  }

  ref.current = { token, value };
  return value;
};
